import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Loader2, TrendingUp, Phone, FileText, Wrench, CheckCircle2, RefreshCw,
  Printer,
  AlertTriangle, ArrowRight, BarChart3, Calendar, Activity,
} from 'lucide-react';

/**
 * Stage Hub / Pipeline overview page.
 *
 * Renders 5 stage cards in an arrow-led flow chart layout. Each card shows
 * the headline metric, a stage-specific cartoon-illustration accent, a
 * forecast-weeks gauge, and per-status pill counts. Clicking a card opens
 * /pipeline/stage/{n} for that stage's filtered worklist.
 *
 * The whole page is intentionally graphic-heavy — Trevor wanted a flow
 * chart he can use to pick his daily focus rather than another list view.
 */

// Service-option dot colours — kept in sync with
// /app/frontend/src/components/ServiceOptionChip.js
const SERVICE_OPTION_DOT = {
  'Reroof':       'bg-slate-500',
  'New Roof':     'bg-emerald-500',
  'Repairs':      'bg-orange-500',
  'Restoration':  'bg-indigo-500',
};

const STAGE_THEMES = {
  1: {
    icon: Phone,
    accent: 'orange',
    title: 'Sales & Quoting',
    focus: 'Leads, appraisals, quotes & follow-ups',
    actions: ['New lead', 'Book appraisal', 'Send quote', 'Follow-up call'],
    illustration: 'phone',
  },
  2: {
    icon: FileText,
    accent: 'emerald',
    title: 'Accepted / Pre-Plan',
    focus: 'Won contracts — confirm specs, plan ahead',
    actions: ['Re-check measurements', 'Confirm with client', 'Schedule into Stage 3'],
    illustration: 'handshake',
  },
  3: {
    icon: Wrench,
    accent: 'blue',
    title: 'Work Booked / Active',
    focus: 'Materials, contractors, safety in motion',
    actions: ['Order materials', 'Confirm contractor', 'Safety plan', 'Notify tenant'],
    illustration: 'truck',
  },
  4: {
    icon: CheckCircle2,
    accent: 'purple',
    title: 'Completion & Invoice',
    focus: 'QA check, contractor pay, invoice',
    actions: ['Quality check', 'Contractor bonus', 'Issue invoice', 'Send certificate'],
    illustration: 'tick',
  },
  5: {
    icon: RefreshCw,
    accent: 'slate',
    title: 'Maintenance / Future',
    focus: 'Re-quote, scheduled maintenance, repeat sales',
    actions: ['Schedule maintenance', 'Re-engage', 'Bulk-route by area'],
    illustration: 'cycle',
  },
};

const ACCENT_CLASSES = {
  orange:  { bg: 'from-orange-50 to-amber-100/30',   ring: 'ring-orange-200',  text: 'text-orange-700',  dot: 'bg-orange-500'  },
  emerald: { bg: 'from-emerald-50 to-emerald-100/30', ring: 'ring-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  blue:    { bg: 'from-blue-50 to-blue-100/30',       ring: 'ring-blue-200',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  purple:  { bg: 'from-purple-50 to-purple-100/30',   ring: 'ring-purple-200',  text: 'text-purple-700',  dot: 'bg-purple-500'  },
  slate:   { bg: 'from-slate-50 to-slate-100/30',     ring: 'ring-slate-200',   text: 'text-slate-700',   dot: 'bg-slate-500'   },
};

export default function PipelineHubPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverStage, setHoverStage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pipeline/overview');
      setOverview(data);
    } catch (e) { setOverview(null); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid="pipeline-loading" className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading pipeline…
      </div>
    );
  }

  if (!overview) return <div className="text-slate-400 text-sm">Failed to load pipeline overview.</div>;

  const stages = overview.stages || [];
  const banners = overview.banners || [];
  const grandTotalCount = stages.reduce((a, s) => a + s.count, 0);
  const grandTotalValue = stages.reduce((a, s) => a + (s.contract_value || 0), 0);

  return (
    <div data-testid="pipeline-hub-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Pipeline Hub</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Your day at a glance — pick a stage, focus there. Stage 5 feeds back into Stage 1 for repeat sales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right text-xs">
            <p className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">In flight</p>
            <p className="font-mono-num font-extrabold text-slate-800">{grandTotalCount.toLocaleString()} jobs · ${(grandTotalValue / 1000).toFixed(1)}k</p>
          </div>
          <div className="text-right text-xs">
            <p className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">Throughput</p>
            <p className="font-mono-num font-extrabold text-slate-800">{overview.avg_weekly_throughput}/wk avg</p>
          </div>
          <button data-testid="pipeline-refresh" onClick={load} className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50" title="Refresh"><RefreshCw size={14} /></button>
          <Link data-testid="pipeline-print-stack-btn" to="/print-stack"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#D35400] text-white rounded-md hover:bg-[#A04000] transition-colors">
            <Printer size={12} /> Print Stack
          </Link>
        </div>
      </div>

      {/* Health banners */}
      {banners.length > 0 && (
        <div data-testid="pipeline-banners" className="space-y-1.5">
          {banners.map((b, i) => (
            <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-md text-xs font-medium ring-1 ${b.severity === 'warn' ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-blue-50 text-blue-800 ring-blue-200'}`}>
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
              <span className="flex-1">{b.msg}</span>
              <button onClick={() => navigate(`/pipeline/stage/${b.stage}`)} className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 hover:bg-white font-semibold flex-shrink-0">Open Stage {b.stage} →</button>
            </div>
          ))}
        </div>
      )}

      {/* Flow chart — Stages 1 → 2 → 3 → 4 with arrows, then Stage 5 below feeding back to Stage 1 */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="grid grid-cols-1 lg:grid-cols-9 gap-3 items-stretch">
          {[1, 2, 3, 4].map((id, idx) => (
            <React.Fragment key={id}>
              <div className="lg:col-span-2">
                <StageCard stage={stages[id - 1]} stageId={id} onHover={setHoverStage} onOpen={() => navigate(`/pipeline/stage/${id}`)} />
              </div>
              {idx < 3 && (
                <div className="hidden lg:flex items-center justify-center text-orange-400">
                  <ArrowRight size={28} strokeWidth={2.5} className="animate-pulse" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Stage 5 — set below as the "feedback loop" */}
        <div className="mt-5 pt-5 border-t border-dashed border-slate-200 grid grid-cols-1 lg:grid-cols-9 gap-3 items-center">
          <div className="lg:col-span-7 text-xs text-slate-500 flex items-center gap-2 font-medium">
            <RefreshCw size={13} className="text-slate-400 flex-shrink-0" />
            <span className="leading-relaxed">
              <strong className="text-slate-700">Future / Maintenance loop:</strong> Paid jobs become future-sales contacts.
              Scheduled maintenance (roof treatment, gutter clean, house wash, driveway clean, chimney clean) gets bulk-routed by area + season.
              <span className="ml-1 text-orange-600">⤴ Feeds back into Stage 1.</span>
            </span>
          </div>
          <div className="lg:col-span-2">
            <StageCard stage={stages[4]} stageId={5} onHover={setHoverStage} onOpen={() => navigate(`/pipeline/stage/5`)} />
          </div>
        </div>
      </div>

      {/* Hover overview panel — shows extra detail without leaving the page */}
      {hoverStage && stages[hoverStage - 1] && (
        <div data-testid="pipeline-hover-detail" className="fixed bottom-6 right-6 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl p-4 z-30 transition-opacity">
          <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${ACCENT_CLASSES[STAGE_THEMES[hoverStage].accent].dot}`} />
            Stage {hoverStage} · {STAGE_THEMES[hoverStage].title}
          </h4>
          <p className="text-[11px] text-slate-500 mb-2">{STAGE_THEMES[hoverStage].focus}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Status breakdown</p>
          <ul className="space-y-0.5 text-xs">
            {(stages[hoverStage - 1].per_status || []).map(s => (
              <li key={s.status} className="flex items-center justify-between">
                <span className="text-slate-700">{s.status}</span>
                <span className="font-mono-num font-semibold text-slate-800">{s.count}</span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-3 mb-1">Today's actions</p>
          <ul className="text-[11px] text-slate-600 space-y-0.5">
            {STAGE_THEMES[hoverStage].actions.map(a => <li key={a}>· {a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}


function StageCard({ stage, stageId, onHover, onOpen }) {
  const theme = STAGE_THEMES[stageId];
  const accent = ACCENT_CLASSES[theme.accent];
  const Icon = theme.icon;
  if (!stage) return null;

  return (
    <button
      data-testid={`pipeline-stage-card-${stageId}`}
      onMouseEnter={() => onHover(stageId)}
      onMouseLeave={() => onHover(null)}
      onClick={onOpen}
      className={`w-full text-left bg-gradient-to-br ${accent.bg} ring-1 ${accent.ring} rounded-xl p-4 transition-transform hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/10 group`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold ${accent.text} opacity-70`}>Stage {stageId}</span>
        </div>
        <Icon size={20} className={`${accent.text} opacity-80`} />
      </div>
      <h3 className={`text-base font-extrabold ${accent.text} font-[Manrope] leading-tight mb-1`}>{theme.title}</h3>
      <p className="text-[10px] text-slate-500 mb-3 leading-tight h-7 overflow-hidden">{theme.focus}</p>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-3xl font-extrabold font-mono-num text-slate-900">{stage.count}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">jobs</span>
      </div>

      <div className="flex items-center gap-1 text-[10px] mb-2">
        <span className={`px-1.5 py-0.5 rounded-full ${accent.text} bg-white/70 font-bold font-mono-num`}>
          ${(stage.contract_value / 1000).toFixed(1)}k
        </span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600 font-semibold font-mono-num">{stage.forecast_weeks}w forecast</span>
      </div>

      {/* Per-status mini-bars */}
      <div className="space-y-0.5">
        {(stage.per_status || []).slice(0, 3).map(s => {
          const max = Math.max(1, ...(stage.per_status || []).map(x => x.count));
          return (
            <div key={s.status} className="flex items-center gap-1.5 text-[10px]">
              <span className="text-slate-500 truncate flex-1" title={s.status}>{s.status}</span>
              <div className="w-10 h-1 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full ${accent.dot}`} style={{ width: `${(s.count / max) * 100}%` }} />
              </div>
              <span className="font-mono-num font-semibold text-slate-700 w-5 text-right">{s.count}</span>
            </div>
          );
        })}
      </div>

      {/* Service-option mix dots — orange Repairs · indigo Restoration ·
          emerald New Roof · slate Reroof. Visualises the workflow mix
          inside the stage at a glance for capacity planning. */}
      {(stage.per_service || []).length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/40 flex items-center gap-1.5 flex-wrap" data-testid={`stage-card-service-mix-${stageId}`}>
          {stage.per_service.slice(0, 4).map(sv => {
            const dot = SERVICE_OPTION_DOT[sv.service_option] || 'bg-slate-500';
            return (
              <span key={sv.service_option} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700" title={`${sv.count} × ${sv.service_option}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                <span className="font-mono-num">{sv.count}</span>
              </span>
            );
          })}
        </div>
      )}

      <p className="text-[10px] font-bold mt-3 text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        Open worklist & advance jobs <ArrowRight size={11} />
      </p>
    </button>
  );
}
